import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {NavLink} from 'react-router-dom';
import {Breadcrumbs} from '~/components/Breadcrumps';
import {Button} from '~/components/Button';
import {SelectInput} from '~/components/Controls/Select';
import {TagsList} from '~/components/Controls/Tags';
import {Progress} from '~/components/Progress';
import {TableSort, TableSortDirection} from '~/components/Table';
import {Title} from '~/components/Title';
import {CSV} from '~/lib/csv';
import {formatDateTime} from '~/lib/date';
import {getApiErrorMessage} from '~/lib/errors/apiError';
import {Intent} from '~/lib/intent';
import {ID} from '~/lib/model';
import {sortByNumber, sortByString} from '~/lib/sort';
import {clientsLinks} from '~/modules/Clients';
import {fetchSensitiveCategoryReport} from '~/modules/Clients/api';
import {SelectAccountManager} from '~/modules/Clients/components/SelectAccountManager';
import {
    getSensitiveCategoryDecisionLabel,
    getSensitiveCategoryLabel,
    ISensitiveCategoryReportRow,
    SensitiveCategory,
    sensitiveCategories,
    SensitiveCategoryDecision,
    sensitiveCategoryDecisions
} from '~/modules/Clients/models/SensitiveCategory';
import {useAccountManagersQuery} from '~/modules/Dictionary/queries';
import {PmpNavigation} from '~/modules/Pmp/components/PmpNavigation';
import style from './Pmp.module.scss';

interface IReportRow extends ISensitiveCategoryReportRow {
    accountManager: string;
}

export const SensitiveCategoriesReportView = () => {
    const [category, setCategory] = useState<SensitiveCategory>(sensitiveCategories[0].value);
    const [decision, setDecision] = useState<SensitiveCategoryDecision | null>(null);
    const [accountManagerId, setAccountManagerId] = useState<ID | null>(null);
    const [rows, setRows] = useState<ISensitiveCategoryReportRow[]>([]);
    const [isPending, setPending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const {data: accountManagers = []} = useAccountManagersQuery();

    const load = useCallback(() => {
        setPending(true);
        setError(null);
        fetchSensitiveCategoryReport(category, decision ?? undefined)
            .then(setRows)
            .catch(e => setError(getApiErrorMessage(e, 'Could not load the report')))
            .finally(() => setPending(false));
    }, [category, decision]);

    useEffect(load, [load]);

    const records: IReportRow[] = useMemo(() => rows
        .filter(row => accountManagerId === null || row.accountManagerId === accountManagerId)
        .map(row => ({
            ...row,
            accountManager: accountManagers.find(manager => manager.id === row.accountManagerId)?.name ?? ''
        })), [rows, accountManagerId, accountManagers]);

    const download = () => CSV.fromDataSource(records.map(row => ({
        sellerId: row.sellerId,
        sellerName: row.sellerName ?? '',
        accountManager: row.accountManager,
        category: getSensitiveCategoryLabel(row.category),
        decision: getSensitiveCategoryDecisionLabel(row.decision),
        exceptionBundles: row.exceptionBundles.join(' '),
        note: row.note ?? '',
        updatedAt: row.updatedAt
    })), ['sellerId', 'sellerName', 'accountManager', 'category', 'decision', 'exceptionBundles', 'note', 'updatedAt'])
        .download(`sensitive-categories-${category}.csv`);

    return <div>
        <div className="page-header-sticky">
            <div className="page-header-breadcrumbs">
                <Breadcrumbs/>
            </div>
            <Title className="page-header-title" toolbar={<>
                <Button responsive
                    disabled={isPending || records.length === 0}
                    onClick={download}
                    leftIcon={<i className='far fa-file-excel'/>}
                >Download CSV</Button>
                <Button intent={Intent.SUCCESS} responsive
                    disabled={isPending}
                    leftIcon={<i className='fa fa-refresh'/>}
                    onClick={load}
                >Refresh</Button>
            </>}>Sensitive Categories</Title>
        </div>
        <PmpNavigation/>
        <div className="layout-content-box">
            <p className={style.reportDescription}>
                Sellers that answered the category, as declared on their Seller page. Sellers who have not answered are
                not eligible and are not listed. An exception app takes the opposite decision from the account.
            </p>
            <form className={style.reportFilter} onSubmit={(e) => e.preventDefault()}>
                <SelectInput
                    items={sensitiveCategories}
                    value={category}
                    onChange={setCategory}
                    placeholder='Category'
                    searchable
                    className={style.filterControl}
                />
                <SelectInput<SensitiveCategoryDecision | null>
                    items={sensitiveCategoryDecisions}
                    value={decision}
                    onChange={setDecision}
                    clearable
                    placeholder='Any decision'
                    className={style.filterControl}
                />
                <SelectAccountManager
                    value={accountManagerId}
                    onChange={setAccountManagerId}
                    clearable
                    searchable
                    placeholder='Any account manager'
                    className={style.filterControl}
                />
            </form>
            {error && <p className={style.reportError}>{error}</p>}
            <TableSort
                records={records}
                defaultSort={{
                    sort: sortByString,
                    field: 'sellerName',
                    direction: TableSortDirection.ASC
                }}
                header
                withPagination
                columns={[{
                    field: 'sellerName',
                    header: 'Seller',
                    sort: sortByString,
                    cell: (row: IReportRow) => <NavLink to={clientsLinks.seller(row.sellerId)}>{row.sellerName || row.sellerId}</NavLink>
                }, {
                    field: 'sellerId',
                    header: 'ID',
                    sort: sortByNumber,
                    small: true
                }, {
                    field: 'accountManager',
                    header: 'Account manager',
                    sort: sortByString,
                    responsive: true
                }, {
                    field: 'decision',
                    header: 'Account',
                    sort: sortByString,
                    cell: (row: IReportRow) => getSensitiveCategoryDecisionLabel(row.decision)
                }, {
                    field: 'exceptionBundles',
                    header: 'App exceptions',
                    cell: (row: IReportRow) => row.exceptionBundles.length
                        ? <TagsList values={row.exceptionBundles} maxItems={3} size="small"/>
                        : <span className={style.muted}>&mdash;</span>
                }, {
                    field: 'note',
                    header: 'Note',
                    responsive: true,
                    cellWrap: 'clamp-2',
                    cell: (row: IReportRow) => row.note?.trim() ? row.note : <span className={style.muted}>&mdash;</span>
                }, {
                    field: 'updatedAt',
                    header: 'Updated',
                    sort: sortByString,
                    cell: (row: IReportRow) => formatDateTime(row.updatedAt)
                }]}
                placeholder={isPending && <Progress/>}
            />
        </div>
    </div>;
};
